class SizeFinderModal extends HTMLElement {
  connectedCallback() {
    if (this.dataset.initialized === 'true') return;
    this.dataset.initialized = 'true';
    this.lastFocusedElement = null;
    this.selectedChoice = this.querySelector('[data-size-finder-choice].is-selected')
      || this.querySelector('[data-size-finder-choice]');

    this.addEventListener('click', (event) => this.handleClick(event));
    this.addEventListener('input', (event) => this.handleInput(event));
    this.addEventListener('keydown', (event) => this.handleKeydown(event));

    const ruler = this.querySelector('[data-size-finder-ruler]');
    ruler?.addEventListener('pointerdown', (event) => this.startRulerDrag(event));
    ruler?.addEventListener('pointermove', (event) => this.moveRuler(event));
    ruler?.addEventListener('pointerup', (event) => this.endRulerDrag(event));
    ruler?.addEventListener('pointercancel', (event) => this.endRulerDrag(event));
  }

  copy(key, fallback = '') {
    return this.dataset[key] || fallback;
  }

  open(trigger, returnFocusElement = trigger) {
    this.lastFocusedElement = returnFocusElement;
    this.cartEditContext = trigger.dataset.cartLineKey
      ? {
          lineKey: trigger.dataset.cartLineKey,
          variantId: Number(trigger.dataset.cartVariantId),
          quantity: Number(trigger.dataset.cartLineQuantity) || 1,
          cartItems: trigger.closest('cart-items-component'),
          cartDrawer: trigger.closest('theme-drawer'),
        }
      : null;

    if (this.cartEditContext) {
      const currentChoice = this.querySelector(
        `[data-size-finder-choice][data-variant-id="${CSS.escape(String(this.cartEditContext.variantId))}"]`
      );
      if (currentChoice) {
        this.selectChoice(currentChoice);
        this.syncRangeToChoice(currentChoice);
      }
    }

    this.hideSameVariantConfirmation(false);
    const status = this.querySelector('[data-size-finder-status]');
    if (status) status.textContent = '';
    this.portalToBody(Boolean(this.cartEditContext));
    this.hidden = false;
    document.body.classList.add('size-finder-is-open');
    // Cart drawer's Change action already has the shopper in an edit flow, so
    // take them straight to measurement. All other Size Finder entry points
    // continue to start with the measuring instructions.
    this.showStep(this.cartEditContext ? 2 : 1);
    requestAnimationFrame(() => this.querySelector('.size-finder__dialog')?.focus());
  }

  close() {
    this.hidden = true;
    document.body.classList.remove('size-finder-is-open');
    this.restorePosition();
    this.lastFocusedElement?.focus();
    this.cartEditContext = null;
  }

  portalToBody(force = false) {
    if ((!force && !window.matchMedia('(min-width: 750px)').matches) || this.parentElement === document.body) return;

    this.originParent = this.parentNode;
    this.originNextSibling = this.nextSibling;
    document.body.append(this);
  }

  restorePosition() {
    if (!this.originParent) return;

    if (!this.originParent.isConnected) {
      this.remove();
      this.originParent = null;
      this.originNextSibling = null;
      return;
    }

    if (this.originNextSibling?.parentNode === this.originParent) {
      this.originParent.insertBefore(this, this.originNextSibling);
    } else {
      this.originParent.append(this);
    }

    this.originParent = null;
    this.originNextSibling = null;
  }

  showStep(stepNumber) {
    this.querySelectorAll('[data-size-finder-step]').forEach((step) => {
      const isActive = step.dataset.sizeFinderStep === String(stepNumber);
      step.hidden = !isActive;
      step.classList.toggle('is-active', isActive);
    });

    if (stepNumber === 2) this.initializeMeasurementRange();
    if (stepNumber === 3) this.updateResult();
    this.querySelector('.size-finder__dialog')?.scrollTo({ top: 0, behavior: 'smooth' });
  }

  selectChoice(choice, animateSelection = false) {
    const previousChoice = this.selectedChoice;
    this.selectedChoice = choice;
    this.querySelectorAll('[data-size-finder-choice]').forEach((button) => {
      const isSelected = button.dataset.optionValueId === choice.dataset.optionValueId;
      button.classList.toggle('is-selected', isSelected);
      button.classList.toggle('is-auto-selected', animateSelection && isSelected && button !== previousChoice);
      button.setAttribute('aria-pressed', String(isSelected));
    });

  }

  syncRangeToChoice(choice) {
    const range = this.querySelector('[data-size-finder-range]');
    const measurement = this.parseMeasurement(choice?.dataset.optionValue || '');
    if (!(range instanceof HTMLInputElement) || !Number.isFinite(measurement) || measurement <= 0) return;

    this.setRulerValue(range, measurement);
  }

  initializeMeasurementRange() {
    const range = this.querySelector('[data-size-finder-range]');
    if (!(range instanceof HTMLInputElement)) return;

    range.dataset.rangeInitialized = 'true';

    // Start every new measurement at the most common post size, rather than
    // inheriting the currently selected product/cart variant.
    this.setRulerValue(range, 3.5);
    this.showRulerHint();
  }

  showRulerHint() {
    const hint = this.querySelector('[data-size-finder-drag-hint]');
    if (!hint || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    if (this.dataset.rulerHintShown === 'true') return;

    this.dataset.rulerHintShown = 'true';
    hint.hidden = false;
    requestAnimationFrame(() => hint.classList.add('is-visible'));
    this.rulerHintTimeout = window.setTimeout(() => this.hideRulerHint(), 1800);
  }

  hideRulerHint() {
    window.clearTimeout(this.rulerHintTimeout);
    this.rulerHintTimeout = null;

    const hint = this.querySelector('[data-size-finder-drag-hint]');
    if (!hint) return;

    hint.classList.remove('is-visible');
    hint.hidden = true;
  }

  handleInput(event) {
    if (!(event.target instanceof HTMLInputElement) || !event.target.matches('[data-size-finder-range]')) return;
    this.updateMeasurementRange(event.target);
  }

  startRulerDrag(event) {
    if (event.button !== undefined && event.button !== 0) return;

    const range = this.querySelector('[data-size-finder-range]');
    const ruler = event.currentTarget;
    if (!(range instanceof HTMLInputElement) || !(ruler instanceof HTMLElement)) return;

    this.hideRulerHint();
    event.preventDefault();
    this.rulerDrag = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startValue: Number(range.value),
      moved: false,
    };
    ruler.classList.add('is-dragging');
    ruler.setPointerCapture?.(event.pointerId);
  }

  moveRuler(event) {
    const drag = this.rulerDrag;
    const range = this.querySelector('[data-size-finder-range]');
    const ruler = event.currentTarget;
    if (!drag || drag.pointerId !== event.pointerId || !(range instanceof HTMLInputElement)) return;

    event.preventDefault();
    const distance = event.clientX - drag.startX;
    if (Math.abs(distance) > 3) {
      drag.moved = true;
      this.hideRulerHint();
    }
    const rulerWidth = Math.max(1, ruler.clientWidth);
    const valueSpan = Number(range.max) - Number(range.min);
    this.setRulerValue(range, drag.startValue - ((distance / rulerWidth) * valueSpan));
  }

  endRulerDrag(event) {
    const drag = this.rulerDrag;
    const range = this.querySelector('[data-size-finder-range]');
    const ruler = event.currentTarget;
    if (!drag || drag.pointerId !== event.pointerId || !(range instanceof HTMLInputElement)) return;

    if (!drag.moved) {
      const bounds = ruler.getBoundingClientRect();
      const valueSpan = Number(range.max) - Number(range.min);
      const distanceFromCenter = event.clientX - (bounds.left + (bounds.width / 2));
      this.setRulerValue(range, drag.startValue + ((distanceFromCenter / bounds.width) * valueSpan));
    }

    ruler.classList.remove('is-dragging');
    ruler.releasePointerCapture?.(event.pointerId);
    this.rulerDrag = null;
  }

  setRulerValue(range, value) {
    const minimum = Number(range.min);
    const maximum = Number(range.max);
    const step = Number(range.step) || 1;
    const clampedValue = Math.min(maximum, Math.max(minimum, value));
    range.value = String(Math.round(clampedValue / step) * step);
    this.updateMeasurementRange(range);
  }

  updateMeasurementRange(range) {
    const value = Number(range.value);
    const minimum = Number(range.min);
    const maximum = Number(range.max);
    const percentage = ((value - minimum) / (maximum - minimum)) * 100;
    const rangeWrap = range.closest('.size-finder__range-wrap');
    rangeWrap?.style.setProperty('--ruler-translate', `${-percentage}%`);

    const measurement = this.querySelector('[data-size-finder-measurement]');
    const decimal = this.querySelector('[data-size-finder-decimal]');
    if (measurement) measurement.textContent = this.formatMeasurement(value);
    if (decimal) decimal.textContent = this.copy('copyDecimal', '≈ [value] inches').replace('[value]', value.toFixed(2));

    const choices = Array.from(this.querySelectorAll('[data-size-finder-choice]')).filter(
      (choice) => !choice.disabled
    );
    const rangeCta = this.querySelector('[data-size-finder-next="3"]');
    if (!choices.length) {
      rangeWrap?.classList.add('is-unavailable');
      if (rangeCta) rangeCta.disabled = true;
      return;
    }

    const measuredChoices = choices
      .map((choice) => {
        const measurement = this.parseMeasurement(choice.dataset.optionValue || '');
        const fitRange = this.parseFitRange(choice.dataset.fitRange || '');
        const effectiveFitRange = fitRange || (Number.isFinite(measurement) && measurement > 0
          ? { minimum: measurement, maximum: measurement }
          : null);
        return { choice, measurement, fitRange: effectiveFitRange };
      })
      .filter((item) => item.fitRange)
      .sort((a, b) => a.fitRange.minimum - b.fitRange.minimum);
    if (!measuredChoices.length) return;

    const tolerance = 0.001;
    const matches = measuredChoices.filter((item) => (
      value >= item.fitRange.minimum - tolerance
      && value <= item.fitRange.maximum + tolerance
    ));
    // Adjacent fit ranges can share an endpoint (for example, 3-3.5 and
    // 3.5-4). At that endpoint, prefer the variant whose displayed size is
    // the exact measurement; otherwise preserve the product's variant order.
    const recommendation = matches.find((item) => Math.abs(item.measurement - value) < tolerance)
      || matches[0]
      || null;
    const isUnavailable = !recommendation;
    if (recommendation) this.selectChoice(recommendation.choice, true);

    rangeWrap?.classList.toggle('is-unavailable', isUnavailable);
    if (rangeCta) rangeCta.disabled = isUnavailable;

    const optionValue = recommendation?.choice.dataset.optionValue || '';
    const recommendationElement = this.querySelector('[data-size-finder-recommendation]');
    const recommendationCopy = this.querySelector('[data-size-finder-recommendation-copy]');
    const ctaSize = this.querySelector('[data-size-finder-cta-size]');

    if (recommendationElement) {
      recommendationElement.classList.toggle('is-unavailable', isUnavailable);
      recommendationElement.textContent = isUnavailable
        ? this.copy('copyNoMatch', 'No matching hook size')
        : this.copy('copyBestMatch', 'Best match - [size] Hook').replace('[size]', optionValue);
    }
    if (recommendationCopy) {
      recommendationCopy.textContent = isUnavailable
        ? this.copy('copyNoMatchDetail', 'No available variant includes this measurement in its fit range')
        : this.copy('copyMatchDetail', 'Your measurement is inside this variant\'s fit range');
    }
    if (ctaSize && recommendation) ctaSize.textContent = optionValue;
  }

  parseMeasurement(value) {
    return this.extractMeasurements(value)[0] || 0;
  }

  parseFitRange(value) {
    const measurements = this.extractMeasurements(value);
    if (!measurements.length) return null;
    if (measurements.length === 1) {
      return { minimum: measurements[0], maximum: measurements[0] };
    }
    return {
      minimum: Math.min(measurements[0], measurements[1]),
      maximum: Math.max(measurements[0], measurements[1]),
    };
  }

  extractMeasurements(value) {
    const unicodeFractions = { '⅛': 0.125, '¼': 0.25, '⅜': 0.375, '½': 0.5, '⅝': 0.625, '¾': 0.75, '⅞': 0.875 };
    let text = String(value);

    text = text.replace(/(\d+)\s+(\d+)\s*\/\s*(\d+)/g, (_, whole, numerator, denominator) => (
      String(Number(whole) + (Number(numerator) / Number(denominator)))
    ));
    text = text.replace(/(\d+)\s*([⅛¼⅜½⅝¾⅞])/g, (_, whole, fraction) => (
      String(Number(whole) + unicodeFractions[fraction])
    ));
    text = text.replace(/[⅛¼⅜½⅝¾⅞]/g, (fraction) => String(unicodeFractions[fraction]));

    return (text.match(/\d+(?:\.\d+)?/g) || []).map(Number);
  }

  formatMeasurement(value) {
    const roundedEighths = Math.round(value * 8);
    const whole = Math.floor(roundedEighths / 8);
    const remainder = roundedEighths % 8;
    const fractions = ['', '1/8', '1/4', '3/8', '1/2', '5/8', '3/4', '7/8'];
    return `${whole}${remainder ? ` ${fractions[remainder]}` : ''}”`;
  }

  updateResult() {
    const choice = this.selectedChoice || this.querySelector('[data-size-finder-choice]');
    if (!choice) return;

    const value = choice.dataset.optionValue || '';
    const price = choice.dataset.price || '';
    this.querySelectorAll('[data-size-finder-result], [data-size-finder-add-size]').forEach((element) => {
      element.textContent = value;
    });

    const priceElement = this.querySelector('[data-size-finder-add-price]');
    if (priceElement) priceElement.textContent = price ? ` — ${price}` : '';

    const isCurrentCartVariant = this.cartEditContext?.variantId === Number(choice.dataset.variantId);
    const label = this.querySelector('[data-size-finder-add-label]');
    const suffix = this.querySelector('[data-size-finder-add-suffix]');
    if (label) label.textContent = this.cartEditContext
      ? (isCurrentCartVariant ? this.copy('copyAddAnother', 'Add Another') : this.copy('copyReplace', 'Replace with'))
      : this.copy('copyAdd', 'Add');
    if (suffix) suffix.textContent = this.cartEditContext && !isCurrentCartVariant
      ? this.copy('copyHook', 'Hook')
      : this.copy('copyHookToCart', 'Hook to Cart');

    this.querySelectorAll('[data-size-finder-alternative]').forEach((button) => {
      button.hidden = button.dataset.optionValueId === choice.dataset.optionValueId;
    });
  }

  syncMainVariant() {
    const choice = this.selectedChoice;
    if (!choice) return false;

    const picker = this.findMainPicker();
    if (!picker) return false;

    const optionValueId = CSS.escape(choice.dataset.optionValueId || '');
    const value = choice.dataset.optionValue || '';
    const radio = picker.querySelector(`fieldset:first-of-type input[data-option-value-id="${optionValueId}"]`);
    const select = picker.querySelector('.variant-option--dropdowns:first-of-type select');

    if (radio instanceof HTMLInputElement) {
      radio.checked = true;
      radio.dispatchEvent(new Event('change', { bubbles: true }));
      return true;
    }

    if (select instanceof HTMLSelectElement) {
      select.value = value;
      select.dispatchEvent(new Event('change', { bubbles: true }));
      return true;
    }

    return false;
  }

  findMainPicker() {
    const productId = this.dataset.productId;
    const scope = this.closest('.shopify-section, dialog') || document;
    return Array.from(scope.querySelectorAll('variant-picker')).find(
      (picker) => picker.dataset.productId === productId
    ) || document.querySelector(`variant-picker[data-product-id="${CSS.escape(productId || '')}"]`);
  }

  async addToCart() {
    const status = this.querySelector('[data-size-finder-status]');
    const addButton = this.querySelector('[data-size-finder-add]');
    if (status) status.textContent = '';

    if (this.cartEditContext) {
      const selectedVariantId = Number(this.selectedChoice?.dataset.variantId);
      if (!selectedVariantId) {
        if (status) status.textContent = this.copy('copyChooseSize', 'Please choose an available size.');
        return;
      }

      if (selectedVariantId === this.cartEditContext.variantId) {
        this.showSameVariantConfirmation();
        return;
      }

      this.updateCartVariant(false);
      return;
    }

    const selectedVariantId = Number(this.selectedChoice?.dataset.variantId);
    if (!selectedVariantId) {
      if (status) status.textContent = this.copy('copyChooseSize', 'Please choose an available size.');
      return;
    }

    if (addButton) addButton.disabled = true;

    // On a product page, keep using the native product form so all product-form
    // integrations continue to run. Homepage product cards do not have one, so
    // add the selected variant directly through Shopify's cart API instead.
    const syncedMainVariant = this.syncMainVariant();
    if (!syncedMainVariant) {
      try {
        if (status) status.textContent = this.copy('copyAdding', 'Adding to cart…');
        await this.addSelectedVariantToCart(selectedVariantId);
        this.close();
        requestAnimationFrame(() => this.openCartDrawer());
      } catch (error) {
        if (status) status.textContent = error?.message || this.copy('copyAddError', 'Unable to add to cart. Please try again.');
      } finally {
        if (addButton) addButton.disabled = false;
      }
      return;
    }

    const productId = this.dataset.productId;
    const scope = this.closest('.shopify-section, dialog') || document;
    const forms = Array.from(scope.querySelectorAll('product-form-component'));
    const productForm = forms.find((form) => form.dataset.productId === productId) || forms[0];
    const nativeButton = productForm?.querySelector('[ref="addToCartButton"]');

    if (!nativeButton) {
      if (status) status.textContent = this.copy('copyAddUnavailable', 'Add to cart is not available for this product.');
      if (addButton) addButton.disabled = false;
      return;
    }

    nativeButton.click();
    window.setTimeout(() => {
      if (addButton) addButton.disabled = false;
      this.close();
    }, 500);
  }

  async addSelectedVariantToCart(variantId) {
    const cartItems = Array.from(document.querySelectorAll('cart-items-component'));
    const sectionIds = cartItems
      .map((item) => item.dataset.sectionId)
      .filter(Boolean);
    const cartAddUrl = window.Theme?.routes?.cart_add_url || '/cart/add.js';
    const response = await fetch(cartAddUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        items: [{ id: variantId, quantity: 1 }],
        sections: sectionIds.join(','),
        sections_url: window.location.pathname,
      }),
    });
    const result = await response.json();

    if (!response.ok || result.status) {
      throw new Error(result.description || result.message || this.copy('copyAddError', 'Unable to add this size to your cart.'));
    }

    let cart = { totalQuantity: 0 };
    try {
      const cartUrl = window.Theme?.routes?.cart_url
        ? `${window.Theme.routes.cart_url}.js`
        : '/cart.js';
      const cartResponse = await fetch(cartUrl, { headers: { Accept: 'application/json' } });
      if (cartResponse.ok) {
        const cartData = await cartResponse.json();
        cart = { ...cartData, totalQuantity: cartData.item_count || 0 };
      }
    } catch (error) {
      // The item was added successfully; a cart refresh failure should not undo it.
    }

    const updateEvent = new CustomEvent('shopify:cart:lines-update', { bubbles: true });
    Object.assign(updateEvent, {
      action: 'add',
      promise: Promise.resolve({
        cart,
        detail: {
          sections: result.sections,
          items: cart.items || [result.item],
          itemCount: cart.totalQuantity,
          source: 'size-finder',
          didError: false,
        },
      }),
    });
    this.dispatchEvent(updateEvent);
  }

  openCartDrawer() {
    const cartDrawer = document.getElementById('cart-drawer');
    if (typeof cartDrawer?.open === 'function') cartDrawer.open();
  }

  showSameVariantConfirmation() {
    const confirmation = this.querySelector('[data-size-finder-confirmation]');
    if (!confirmation) return;
    confirmation.hidden = false;
    requestAnimationFrame(() => confirmation.querySelector('.size-finder__confirmation-dialog')?.focus());
  }

  hideSameVariantConfirmation(restoreFocus = true) {
    const confirmation = this.querySelector('[data-size-finder-confirmation]');
    if (!confirmation) return;
    confirmation.hidden = true;
    if (restoreFocus) this.querySelector('[data-size-finder-add]')?.focus();
  }

  async updateCartVariant(incrementIfSame) {
    const context = this.cartEditContext;
    const selectedVariantId = Number(this.selectedChoice?.dataset.variantId);
    const status = this.querySelector('[data-size-finder-status]');
    const addButton = this.querySelector('[data-size-finder-add]');
    const confirmButton = this.querySelector('[data-size-finder-confirm-add]');
    if (!context || !selectedVariantId) return;

    if (!context.cartItems || typeof context.cartItems.replaceVariant !== 'function') {
      if (status) status.textContent = this.copy('copyCartUnavailable', 'Cart editing is temporarily unavailable. Please try again.');
      this.hideSameVariantConfirmation(false);
      return;
    }

    if (status) status.textContent = incrementIfSame
      ? this.copy('copyAddingAnother', 'Adding another…')
      : this.copy('copyUpdatingCart', 'Updating your cart…');
    if (addButton) addButton.disabled = true;
    if (confirmButton) confirmButton.disabled = true;

    try {
      await context.cartItems.replaceVariant({
        lineKey: context.lineKey,
        currentVariantId: context.variantId,
        newVariantId: selectedVariantId,
        quantity: context.quantity,
        incrementIfSame,
      });
      this.close();
      const cartDrawer = document.getElementById(context.cartDrawer?.id || 'cart-drawer');
      if (typeof cartDrawer?.open === 'function') cartDrawer.open();
    } catch (error) {
      if (status) status.textContent = error?.message || this.copy('copyUpdateError', 'Unable to update your cart. Please try again.');
      this.hideSameVariantConfirmation(false);
      if (addButton) addButton.disabled = false;
      if (confirmButton) confirmButton.disabled = false;
    }
  }

  handleClick(event) {
    const target = event.target.closest(
      'button, [data-size-finder-close], [data-size-finder-confirm-cancel]'
    );
    if (!target) return;

    if (target.hasAttribute('data-size-finder-close')) {
      this.close();
      return;
    }

    if (target.hasAttribute('data-size-finder-confirm-cancel')) {
      this.hideSameVariantConfirmation();
      return;
    }

    if (target.hasAttribute('data-size-finder-confirm-add')) {
      this.updateCartVariant(true);
      return;
    }

    const choice = target.closest('[data-size-finder-choice], [data-size-finder-alternative]');
    if (choice) {
      const sourceChoice = this.querySelector(
        `[data-size-finder-choice][data-option-value-id="${CSS.escape(choice.dataset.optionValueId || '')}"]`
      );
      if (sourceChoice) {
        this.selectChoice(sourceChoice);
        this.syncRangeToChoice(sourceChoice);
      }
      if (target.hasAttribute('data-size-finder-alternative')) {
        this.updateResult();
      }
      return;
    }

    if (target.hasAttribute('data-size-finder-next')) {
      this.showStep(Number(target.dataset.sizeFinderNext));
      return;
    }

    if (target.hasAttribute('data-size-finder-add')) this.addToCart();
  }

  handleKeydown(event) {
    if (event.key === 'Escape') this.close();
  }
}

if (!customElements.get('size-finder-modal')) {
  customElements.define('size-finder-modal', SizeFinderModal);
}

if (!window.sizeFinderTriggerBound) {
  window.sizeFinderTriggerBound = true;
  document.addEventListener('click', async (event) => {
    const trigger = event.target.closest('[data-size-finder-open]');
    if (!trigger) return;
    const modal = document.getElementById(trigger.dataset.sizeFinderOpen);
    if (!(modal instanceof SizeFinderModal)) return;

    const drawer = trigger.closest('theme-drawer');
    const returnFocusElement = drawer
      ? document.querySelector(`[aria-controls="${CSS.escape(drawer.id)}"]`) || trigger
      : trigger;

    if (drawer?.isOpen && typeof drawer.close === 'function') {
      await drawer.close();
    }

    modal.open(trigger, returnFocusElement);
  });
}
